import { Box } from '@mui/material';
import type { ReactNode } from 'react';

import {
  CATEGORY_ACCENT,
  DEPLETIONS_SECTIONS,
  type DepletionsSection,
} from '../data/depletionsSections';
import { AccountMonthlyHeatmap } from './AccountMonthlyHeatmap';
import { CollapsibleSection } from './CollapsibleSection';
import { DepletionsDashboardGuide } from './DepletionsDashboardGuide';
import { DepletionsGeographicBreakdown } from './DepletionsGeographicBreakdown';
import { DepletionsKpiStrip } from './DepletionsKpiStrip';
import { DepletionsMonthlyOrders } from './DepletionsMonthlyOrders';
import { DepletionsPremisesMix } from './DepletionsPremisesMix';
import { DepletionsProductPerformance } from './DepletionsProductPerformance';
import { DepletionsTrendChart } from './DepletionsTrendChart';
import { DepletionsWhatIfSimulator } from './DepletionsWhatIfSimulator';
import { FollowUpRankingInfo, FollowUpTracker } from './FollowUpTracker';
import { GrowthDeclineSection } from './GrowthDeclineSection';
import { NewVsLostAccounts } from './NewVsLostAccounts';
import { SectionExportButton, SectionExportProvider } from './SectionExport';
import { TopAccountsTable } from './TopAccountsTable';

/**
 * Body renderer per catalog section id.
 *
 * The section's *metadata* (title, description, category, icon, and the
 * user-guide copy) lives in `data/depletionsSections.ts` so the User
 * Guide documents exactly what the dashboard renders. Only the actual
 * React body lives here — it can't live in a data module without
 * dragging every chart component into the guide's bundle.
 *
 * Adding a section: add the catalog entry, then add its body here. A
 * catalog entry with no body is skipped rather than crashing.
 */
const SECTION_BODIES: Record<string, ReactNode> = {
  orders: <DepletionsMonthlyOrders />,
  'premises-mix': <DepletionsPremisesMix />,
  'follow-up-tracker': <FollowUpTracker />,
  'monthly-trend': <DepletionsTrendChart />,
  'product-performance': <DepletionsProductPerformance />,
  'state-breakdown': <DepletionsGeographicBreakdown />,
  'top-accounts': <TopAccountsTable />,
  'activity-heatmap': <AccountMonthlyHeatmap />,
  'new-vs-lost': <NewVsLostAccounts />,
  'growth-decline': <GrowthDeclineSection />,
  'what-if': <DepletionsWhatIfSimulator />,
};

/** Extra header controls, keyed by section id. */
const SECTION_HEADER_ACTIONS: Record<string, ReactNode> = {
  'follow-up-tracker': <FollowUpRankingInfo />,
};

/**
 * Depletions sub-view of the Sales page — the retail pull-through side.
 *
 * The guide + top-line KPI strip stay visible as the at-a-glance
 * overview; every analytical section below is collapsed by default and
 * grouped by category, so the page opens as a clean, scannable menu the
 * user expands on demand rather than one long always-expanded scroll.
 */
export function DepletionsView() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <DepletionsDashboardGuide />
      <DepletionsKpiStrip />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {DEPLETIONS_SECTIONS.map((section: DepletionsSection) => {
          const body = SECTION_BODIES[section.id];
          if (!body) return null;
          return (
            // `id` makes each section deep-linkable (/sales/depletions#section-orders)
            // and gives the screenshot capture script a stable handle to
            // expand exactly the section it needs to photograph.
            <Box key={section.id} id={`section-${section.id}`} data-section-id={section.id}>
              {/* Each section gets its own export slot: the open body
                  registers its rows, the header shows an Export button. */}
              <SectionExportProvider about={{ description: section.guide.what }}>
                <CollapsibleSection
                  category={section.category}
                  title={section.title}
                  icon={<section.Icon />}
                  description={section.description}
                  accent={CATEGORY_ACCENT[section.category]}
                  headerAction={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 } }}>
                      <SectionExportButton accent={CATEGORY_ACCENT[section.category]} />
                      {SECTION_HEADER_ACTIONS[section.id]}
                    </Box>
                  }
                >
                  {body}
                </CollapsibleSection>
              </SectionExportProvider>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
